// Plain-words wording shared by the pages.

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const two = (n: number) => String(n).padStart(2, '0');

/** "29 Sep, 13:47" in the viewer's own time. */
export const when = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const d = new Date(iso);
  return `${d.getDate()} ${MONTHS[d.getMonth()]}, ${two(d.getHours())}:${two(d.getMinutes())}`;
};

/** "29 Sep 2027". Plain dates (YYYY-MM-DD) stay the same day everywhere. */
export const day = (iso: string | null | undefined): string => {
  if (!iso) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
    const [y, m, dd] = iso.split('-').map(Number);
    return `${dd} ${MONTHS[m - 1]} ${y}`;
  }
  const d = new Date(iso);
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};

export function ago(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return 'never';
  const mins = Math.round((now - Date.parse(iso)) / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `${hours} h ago`;
  return day(iso);
}

/** The ops tablet counts as online if it checked in within the last 5 minutes. */
export const tabletOnline = (lastSeen: string | null | undefined, now = Date.now()): boolean => !!lastSeen && now - Date.parse(lastSeen) < 5 * 60_000;

export const LEVELS: Record<string, { name: string; colour: string }> = {
  alert: { name: 'Be alert', colour: 'var(--amber)' },
  shelter: { name: 'Take shelter now', colour: 'var(--red)' },
  evacuate: { name: 'Evacuate', colour: 'var(--red)' },
  clear: { name: 'All clear', colour: 'var(--green)' },
  false_alarm: { name: 'False alarm', colour: 'var(--green)' },
};

export const phones = (ios: number, android: number): string => `${ios} iPhone · ${android} Android`;

/** One line for each kind of log entry. */
export function logLine(action: string, d: Record<string, unknown>): string {
  const s = (k: string) => (typeof d[k] === 'string' ? (d[k] as string) : '');
  const by = s('by') ? ` · ${s('by')}` : '';
  switch (action) {
    case 'site.setup_started':
      return `Set-up started${by}`;
    case 'site.created':
      return 'Site went live';
    case 'bundle.published':
      return `Version ${d.version} sent to the tablet and phones`;
    case 'change.sent':
      return `Change sent to the ops tablet for approval: ${s('summary')}${by}`;
    case 'change.approved':
      return `Change approved${by}`;
    case 'change.declined':
      return `Change declined${by}${s('note') ? ` · “${s('note')}”` : ''}`;
    case 'change.withdrawn':
      return `Change withdrawn${by}`;
    case 'approver.invited':
      return `Head office approver invited: ${s('name')}${by}`;
    case 'approver.passkey_added':
      return `${s('name')} set up their passkey${s('device') ? ` (${s('device')})` : ''}`;
    case 'approver.removed':
      return `Head office approver removed: ${s('name')}${by}`;
    case 'tablet.paired':
      return `Ops tablet paired: ${s('label')}${s('model') ? ` (${s('model')})` : ''}${by}`;
    case 'alert.received': {
      const zones = Array.isArray(d.zones) ? (d.zones as string[]).join(', ') : '';
      const level = d.test ? 'Monthly test' : (LEVELS[s('level')]?.name ?? s('level'));
      return `${level} · zone ${zones} · from the ops tablet`;
    }
    case 'signed_in':
      return `${s('who')} signed in`;
    case 'subscription.updated':
      return `Subscription dates set: renews ${day(s('renews'))}${by}`;
    case 'subscription.renewed':
      return `Renewal recorded: now renews ${day(s('to'))}${by}`;
    case 'subscription.reminder':
      return `Renewal reminder sent to the Z.A.S. team (${d.daysBefore} days)`;
    case 'site.switched_off':
      return `Site switched off: ${s('reason')}${by}`;
    case 'site.switched_on':
      return `Site switched back on${by}`;
    case 'map.picture':
      return s('source') === 'client' ? `Map picture: the client's own map uploaded${by}` : `Map picture: satellite picture from ${day(s('captured'))}${by}`;
    case 'invoice.added':
      return `Invoice ${s('number')} added (${s('amount')}, ${s('status')})${by}`;
    case 'invoice.status':
      return `Invoice ${s('number')} marked ${s('status')}${by}`;
    default:
      return action;
  }
}
